import { decode } from 'he';
import type { SourceId } from '../../../constants';
import { DECODED_HASHTAG_REGEX, URL_REGEX } from '../../../constants';

type TokenTypesKey = 'newline' | 'url' | 'email' | 'hashtag' | 'username';

const createA = (link: string, anchor?: string, withoutPreview = false) => {
  const linkElement = document.createElement('a');

  linkElement.setAttribute('href', link);
  linkElement.setAttribute('target', '_blank');
  linkElement.setAttribute('rel', 'noreferrer noopener nofollow');
  linkElement.textContent = anchor || link;

  if (withoutPreview) {
    linkElement.dataset.withoutPreview = '';
  }

  return linkElement.outerHTML;
};

const tokenTypes: Record<
  TokenTypesKey,
  {
    pattern: RegExp;
    handle: (content: string, provider: SourceId) => string;
  }
> = {
  newline: {
    pattern: /\r\n|\n|\r/g,
    handle: () => '<br />'
  },
  url: {
    pattern: new RegExp(URL_REGEX, 'g'),
    handle: (content) => createA(content)
  },
  email: {
    pattern:
      /(([^<>()[\]\\.,;:\s@?/§§__TOKEN(\d+)__§§/"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/g,
    handle: (content) => createA(`mailto:${content}`, content)
  },
  hashtag: {
    pattern: DECODED_HASHTAG_REGEX,
    handle: (content, provider) => {
      const value = content.substring(1);

      let url;

      switch (provider) {
        case 'instagram':
          url = `https://www.instagram.com/explore/tags/${value.toLowerCase()}/`;
          break;

        case 'linkedin':
          const formattedHashtag = value
            ?.toLowerCase()
            ?.replace(/[\u200B-\u200D\uFEFF]/g, '');

          url = `https://www.linkedin.com/feed/hashtag/${formattedHashtag}/`;
          break;

        case 'tumblr':
          url = `https://www.tumblr.com/tagged/${value}`;
          break;

        case 'twitter':
          url = `https://x.com/search?q=(%23${value})&src=typed_query`;

          return createA(url, content, true);

        case 'facebook':
          url = `https://www.facebook.com/hashtag/${value}`;
          break;

        case 'vkontakte':
          url = `https://vk.com/feed?section=search&q=%23${value}`;
          break;

        case 'pinterest':
          url = `https://www.pinterest.com/search/pins/?q=%23${value}&rs=hashtag_closeup`;
          break;

        case 'tiktok':
          url = `https://www.tiktok.com/tag/${value}`;
          break;

        case 'vimeo':
          url = `https://vimeo.com/tag:${value}`;
          break;

        case 'rss':
          return `#${value}`;

        case 'youtube':
          url = `https://www.youtube.com/results?search_query=%23${value}`;
          break;

        default:
          console.warn(`Unknown provider "${provider}"`);

          return '';
      }

      return createA(url, content);
    }
  },
  username: {
    pattern: /@[\p{L}\p{N}_]((?!\.{2,})[\p{L}\p{N}_.-]?)+[\p{L}\p{N}_]/giu,
    handle: (content, provider) => {
      const value = content.substring(1);

      let url;

      switch (provider) {
        case 'instagram':
          url = `https://www.instagram.com/${value}/`;
          break;

        case 'tumblr':
          url = `https://${value}.tumblr.com/`;
          break;

        case 'twitter':
          url = `https://x.com/${value}`;

          return createA(url, content, true);

        case 'facebook':
          url = `https://www.facebook.com/${value}`;
          break;

        case 'vkontakte':
          url = `https://vk.com/${value}`;
          break;

        case 'pinterest':
          url = `https://www.pinterest.com/${value}`;
          break;

        case 'tiktok':
          url = `https://www.tiktok.com/@${value}`;
          break;

        case 'vimeo':
          url = `https://vimeo.com/${value}`;
          break;

        case 'rss':
        case 'linkedin':
          return '';

        case 'youtube':
          url = `https://www.youtube.com/user/${value}`;
          break;

        default:
          console.warn(`Unknown provider "${provider}"`);

          return '';
      }

      return createA(url, content);
    }
  }
};

const decodedProviders = ['rss', 'pinterest'];

export function formatText(
  input: string | null | undefined,
  provider: SourceId
) {
  if (!input || typeof input !== 'string') {
    return;
  }

  const tokens: Array<[TokenTypesKey, string]> = [];
  let template = input.trim();

  if (decodedProviders.includes(provider)) {
    template = decode(template);
  }

  const isHTML = /<\/?[a-z][\s\S]*>/i;
  if (isHTML.test(template)) {
    return template.replace(/(<img(\/?[^>]+)>)/g, '');
  }

  // parse tokens
  Object.entries(tokenTypes).forEach(([key, { pattern }]) => {
    template = template.replace(pattern, (content) => {
      const id = tokens.push([key as TokenTypesKey, content]);

      return `§§__TOKEN${id}__§§`;
    });
  });

  // interpolate tokens
  return template.replace(
    /§§__TOKEN(\d+)__§§/g,
    (_match: string, id: string) => {
      const index = parseInt(id, 10) - 1;
      const [key, content] = tokens[index];

      return tokenTypes[key].handle(content, provider);
    }
  );
}
